import { useForm, type ReactFormExtendedApi } from "@tanstack/react-form";
import { z } from "zod";
import { useState, useEffect, type ReactNode } from "react";
import { Button } from '../../Button';
import { Flex } from '@radix-ui/themes';

/**
 * Catch-all TanStack Form React API type. See FormField for rationale.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyReactFormApi = ReactFormExtendedApi<any, any, any, any, any, any, any, any, any, any, any, any>

interface FormProps<T> {
  schema: z.ZodSchema<T>;
  defaultValues: T;
  onSubmit: (data: T) => void | Promise<void>;
  children: (form: AnyReactFormApi) => ReactNode;
  submitText?: string;
  loadingText?: string;
  className?: string;
}

export const Form = <T extends Record<string, unknown>>({
  schema,
  defaultValues,
  onSubmit,
  children,
  submitText = "Submit",
  loadingText = "Submitting...",
  className = "",
}: FormProps<T>) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmitForm, setCanSubmitForm] = useState(false);

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = schema.safeParse(value);
        if (!result.success) {
          return result.error.issues;
        }
        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        await onSubmit(value);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Update canSubmit state whenever form values change
  useEffect(() => {
    const checkCanSubmit = () => {
      const hasAllValues = Object.keys(defaultValues as Record<string, unknown>).every((key) => {
        const value = form.getFieldValue(key as keyof T & string);
        return value && String(value).trim() !== "";
      });
      setCanSubmitForm(hasAllValues && !isSubmitting);
    };

    // Check immediately
    checkCanSubmit();

    // Check periodically for form changes
    const interval = setInterval(checkCanSubmit, 100);

    return () => clearInterval(interval);
  }, [form, defaultValues, isSubmitting]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className={className}
    >
      {children(form)}

      <Flex direction="column" gap="4" className="mt-6">
        <Button
          type="submit"
          disabled={!canSubmitForm}
          loading={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? loadingText : submitText}
        </Button>
      </Flex>
    </form>
  );
};
